// Yolcu / çalışan / veli uygulaması sözleşmesi (P01–P39, P54–P70): girdiler Zod, yanıtlar tip; sefer durumu türetimi.
import { z } from 'zod';
import {
  COMPLAINT_CATEGORIES,
  REQUEST_TYPES,
  type BoardingType,
  type ComplaintCategory,
  type ComplaintStatus,
  type Direction,
  type PassengerKind,
  type RequestStatus,
  type RequestType,
  type TripStatus,
} from '../enums';

const IsoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı');

// ───────── yolcunun sefer durumu (ana sayfa kartı, canlı takip) ─────────

export const PASSENGER_TRIP_STATES = [
  'NOT_STARTED',
  'ON_THE_WAY',
  'DELAYED',
  'APPROACHING',
  'ARRIVED',
  'BOARDED',
  'MISSED',
  'COMPLETED',
  'CANCELLED',
  'ABSENT',
] as const;
export type PassengerTripState = (typeof PASSENGER_TRIP_STATES)[number];

export const PASSENGER_TRIP_STATE_LABELS: Record<PassengerTripState, string> = {
  NOT_STARTED: 'Henüz başlamadı',
  ON_THE_WAY: 'Servis yolda',
  DELAYED: 'Gecikme',
  APPROACHING: 'Yaklaşıyor',
  ARRIVED: 'Durağınızda',
  BOARDED: 'Serviste',
  MISSED: 'Durağınızı geçti',
  COMPLETED: 'Tamamlandı',
  CANCELLED: 'İptal',
  ABSENT: 'Bugün binmiyorsunuz',
};

/** Yaklaşma eşiği: yolcuya göre "yaklaşıyor" (geofence 300 m veya ≤ 3 dk). */
export const APPROACH_M = 300;
export const APPROACH_MIN = 3;

export function passengerTripState(i: {
  status: TripStatus;
  delayMin: number;
  absent: boolean;
  boarding: BoardingType | null;
  myArrivedAt: string | null;
  myDepartedAt: string | null;
  remainingM: number | null;
  etaMin: number | null;
  delayWarnMin?: number;
}): PassengerTripState {
  if (i.status === 'CANCELLED') return 'CANCELLED';
  if (i.absent && !i.boarding) return 'ABSENT';
  if (i.boarding === 'BOARDED') return i.status === 'COMPLETED' ? 'COMPLETED' : 'BOARDED';
  if (i.boarding === 'ALIGHTED' || i.boarding === 'DELIVERED' || i.status === 'COMPLETED') return 'COMPLETED';
  if (i.boarding === 'NO_SHOW' || i.boarding === 'NOT_BOARDED') return 'MISSED';
  if (i.status === 'SCHEDULED' || i.status === 'READY') return 'NOT_STARTED';
  if (i.myDepartedAt) return 'MISSED';
  if (i.myArrivedAt) return 'ARRIVED';
  if ((i.remainingM !== null && i.remainingM <= APPROACH_M) || (i.etaMin !== null && i.etaMin <= APPROACH_MIN))
    return 'APPROACHING';
  return i.delayMin >= (i.delayWarnMin ?? 5) ? 'DELAYED' : 'ON_THE_WAY';
}

// ───────── yanıtlar ─────────

export interface DriverPublic {
  name: string;
  initials: string;
  verified: boolean;
  rating: number | null;
}

export interface HomeTrip {
  id: string;
  status: TripStatus;
  state: PassengerTripState;
  direction: Direction;
  date: string;
  plannedStartAt: string;
  myStopPlannedAt: string;
  etaAt: string;
  etaMin: number | null;
  delayMin: number;
  remainingM: number | null;
  /** aracın hat üzerindeki konumu (m, başlangıçtan) — harita dilimleme; sefer başlamadıysa null */
  alongM: number | null;
  /** durağımın hat üzerindeki konumu (m) */
  myAlongM: number;
  progressPct: number;
  plate: string;
  vehicle: string;
  vehicleColor: string;
  driver: DriverPublic;
  guide: string | null;
  updatedAt: string | null;
  ready: boolean;
  /** şoförün durakta bekleme süresi (dk) */
  waitMin: number;
  nextStopName: string | null;
  stopsBeforeMine: number;
  occupancy: { boarded: number; total: number };
  position: { lat: number; lng: number; heading: number } | null;
  polyline: string;
  myStop: { lat: number; lng: number };
}

export interface HomeService {
  passengerId: string;
  kind: 'SELF' | 'CHILD';
  passengerKind: PassengerKind;
  firstName: string;
  lastName: string;
  title: string;
  routeCode: string;
  routeName: string;
  institution: string;
  myStop: { id: string; name: string; address: string | null; walkingM: number | null } | null;
  trip: HomeTrip | null;
  nextTrip: { id: string; date: string; plannedStartAt: string; myStopPlannedAt: string; direction: Direction } | null;
  absence: { id: string; scope: AbsenceScope; date: string } | null;
  /** veli kartı için çocuğun anlık durumu (okul modülü) */
  childStatus: {
    state: 'HOME' | 'WAITING' | 'ON_BOARD' | 'AT_SCHOOL' | 'DELIVERED';
    label: string;
    at: string | null;
  } | null;
  returnAt: string | null;
}

export type DayRide = 'RIDE' | 'OFF' | 'ABSENT' | 'DONE' | 'MISSED' | 'NONE';

export interface WeekDay {
  date: string;
  dow: number;
  label: string;
  today: boolean;
  morning: DayRide;
  evening: DayRide;
}

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  read: boolean;
  at: string;
}

export interface PassengerHome {
  firstName: string | null;
  services: HomeService[];
  week: WeekDay[];
  notifications: NotificationItem[];
  unread: number;
  /** eşleşme bekleyen talep (P13) */
  pendingMatch: { id: string; number: string; routeName: string | null; createdAt: string } | null;
}

// ───────── girdiler ─────────

export const ABSENCE_SCOPES = ['MORNING', 'EVENING', 'ALL_DAY'] as const;
export type AbsenceScope = (typeof ABSENCE_SCOPES)[number];
export const ABSENCE_SCOPE_LABELS: Record<AbsenceScope, string> = {
  MORNING: 'Sabah',
  EVENING: 'Akşam',
  ALL_DAY: 'Tüm gün',
};

export const AbsenceInput = z.object({
  passengerId: z.string(),
  scope: z.enum(ABSENCE_SCOPES),
  from: IsoDay,
  to: IsoDay.optional(),
  reason: z.string().trim().max(200).optional(),
});
export type AbsenceInput = z.infer<typeof AbsenceInput>;

export const ReadyInput = z.object({ passengerId: z.string() });

export const WeeklyPlanInput = z.object({
  passengerId: z.string(),
  days: z
    .array(z.object({ dayOfWeek: z.number().int().min(1).max(7), morning: z.boolean(), evening: z.boolean() }))
    .min(1)
    .max(7),
  repeat: z.boolean().default(true),
});
export type WeeklyPlanInput = z.infer<typeof WeeklyPlanInput>;

export const DayPlanInput = z.object({
  passengerId: z.string(),
  morning: z.boolean().optional(),
  evening: z.boolean().optional(),
  reason: z.string().trim().max(200).optional(),
});
export type DayPlanInput = z.infer<typeof DayPlanInput>;

export interface CalendarDay {
  date: string;
  morning: DayRide;
  evening: DayRide;
  morningTime: string | null;
  eveningTime: string | null;
  note: string | null;
}

export interface DayDetail {
  date: string;
  passengerId: string;
  morning: {
    ride: DayRide;
    time: string | null;
    tripId: string | null;
    stop: string | null;
    plate: string | null;
    source: string | null;
  };
  evening: {
    ride: DayRide;
    time: string | null;
    tripId: string | null;
    stop: string | null;
    plate: string | null;
    source: string | null;
  };
}

export const AlternativeSearchInput = z.object({
  passengerId: z.string(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  direction: z.enum(['OUTBOUND', 'INBOUND']).default('OUTBOUND'),
  date: IsoDay,
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  maxWalkM: z.number().int().min(100).max(2000).default(900),
});
export type AlternativeSearchInput = z.infer<typeof AlternativeSearchInput>;

export interface AlternativeResult {
  tripId: string;
  routeCode: string;
  routeName: string;
  plate: string;
  stopId: string;
  stopName: string;
  walkingM: number;
  departureAt: string;
  arrivalAt: string;
  freeSeats: number;
  own: boolean;
}

export const ReservationInput = z.object({ passengerId: z.string(), tripId: z.string(), stopId: z.string() });

export const ADDRESS_LABELS = ['HOME', 'WORK', 'MOTHER', 'FATHER', 'OTHER'] as const;
export type AddressLabel = (typeof ADDRESS_LABELS)[number];
export const ADDRESS_LABEL_NAMES: Record<AddressLabel, string> = {
  HOME: 'Ev',
  WORK: 'İş',
  MOTHER: 'Anne',
  FATHER: 'Baba',
  OTHER: 'Diğer',
};

export const AddressInput = z.object({
  label: z.enum(ADDRESS_LABELS),
  title: z.string().trim().min(2, 'Adres adı en az 2 karakter olmalı').max(60),
  line: z.string().trim().min(5, 'Adres en az 5 karakter olmalı').max(200),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  isDefault: z.boolean().optional(),
});
export type AddressInput = z.infer<typeof AddressInput>;

export interface AddressItem extends AddressInput {
  id: string;
  isDefault: boolean;
}

export interface NearestStop {
  stopId: string;
  name: string;
  routeCode: string;
  routeName: string;
  walkingM: number;
  plannedTime: string | null;
  own: boolean;
}

export const RequestInput = z.object({
  type: z.enum(REQUEST_TYPES),
  passengerId: z.string().optional(),
  institutionId: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  address: z.string().trim().max(200).optional(),
  destination: z.string().trim().max(120).optional(),
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  date: IsoDay.optional(),
  until: IsoDay.optional(),
  note: z.string().trim().max(500).optional(),
  suggestedStopId: z.string().optional(),
});
export type RequestInput = z.infer<typeof RequestInput>;

export interface RequestItem {
  id: string;
  number: string;
  type: RequestType;
  status: RequestStatus;
  summary: string;
  createdAt: string;
  updatedAt: string;
  decisionNote: string | null;
  payload: Record<string, unknown>;
  steps: { status: RequestStatus; label: string; done: boolean; current: boolean }[];
}

export const ComplaintInput = z.object({
  category: z.enum(COMPLAINT_CATEGORIES),
  text: z.string().trim().min(10, 'Lütfen biraz daha ayrıntı yazın (en az 10 karakter).').max(2000),
  tripId: z.string().optional(),
  passengerId: z.string().optional(),
  photoFileIds: z.array(z.string()).max(4).default([]),
});
export type ComplaintInput = z.infer<typeof ComplaintInput>;

export interface ComplaintItem {
  id: string;
  number: string;
  category: ComplaintCategory;
  status: ComplaintStatus;
  text: string;
  createdAt: string;
  updatedAt: string;
  trip: { id: string; date: string; routeCode: string; routeName: string; plate: string } | null;
  photos: string[];
  events: { type: string; body: string | null; at: string }[];
  resolution: string | null;
}

export const RATING_TAGS = ['PUNCTUALITY', 'CLEANLINESS', 'DRIVING', 'AIR_CONDITIONING', 'COMFORT'] as const;
export type RatingTag = (typeof RATING_TAGS)[number];
export const RATING_TAG_LABELS: Record<RatingTag, string> = {
  PUNCTUALITY: 'Dakiklik',
  CLEANLINESS: 'Temizlik',
  DRIVING: 'Sürüş',
  AIR_CONDITIONING: 'Klima',
  COMFORT: 'Konfor',
};

export const RatingInput = z.object({
  stars: z.number().int().min(1).max(5),
  tags: z.array(z.enum(RATING_TAGS)).max(5).default([]),
  comment: z.string().trim().max(500).optional(),
});
export type RatingInput = z.infer<typeof RatingInput>;

export interface PassengerTripItem {
  id: string;
  date: string;
  direction: Direction;
  status: TripStatus;
  routeCode: string;
  routeName: string;
  plate: string;
  passengerId: string;
  passengerName: string;
  myStop: string | null;
  plannedAt: string | null;
  boarding: BoardingType | null;
  boardedAt: string | null;
  delayMin: number;
  rated: boolean;
}

export interface PassengerTripDetail extends PassengerTripItem {
  vehicle: string;
  driver: DriverPublic;
  distanceKm: number | null;
  durationMin: number | null;
  plannedPolyline: string;
  actualPolyline: string | null;
  timeline: { time: string; title: string; sub: string | null; state: 'done' | 'current' | 'alert' | 'pending' }[];
  rating: { stars: number; tags: string[]; comment: string | null } | null;
}

export const MATCH_METHODS = ['QR', 'PLATE', 'INVITE', 'INSTITUTION'] as const;
export type MatchMethod = (typeof MATCH_METHODS)[number];

export const MatchLookupInput = z.object({ method: z.enum(MATCH_METHODS), value: z.string().trim().min(2).max(120) });
export type MatchLookupInput = z.infer<typeof MatchLookupInput>;

export interface MatchRoute {
  routeId: string;
  code: string;
  name: string;
  institutionId: string;
  institution: string;
  morningAt: string | null;
  eveningAt: string | null;
  stops: number;
}

export interface MatchLookupResult {
  vehicle: { plate: string; make: string; model: string; color: string; company: string } | null;
  institutions: { id: string; name: string; kind: string | null }[];
  routes: MatchRoute[];
}

export const MatchRequestInput = z.object({
  institutionId: z.string(),
  routeId: z.string().optional(),
  method: z.enum(MATCH_METHODS),
  who: z.enum(['SELF', 'CHILD']).default('SELF'),
  child: z
    .object({
      firstName: z.string().trim().min(2).max(60),
      lastName: z.string().trim().min(2).max(60),
      className: z.string().trim().max(10).optional(),
    })
    .optional(),
  addressId: z.string().optional(),
  note: z.string().trim().max(300).optional(),
});
export type MatchRequestInput = z.infer<typeof MatchRequestInput>;

export interface MatchRequestStatus {
  id: string;
  number: string;
  status: RequestStatus;
  institution: string;
  routeName: string | null;
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  passengerId: string | null;
}

export interface PassengerService {
  passengerId: string;
  kind: 'SELF' | 'CHILD';
  name: string;
  institution: string;
  company: string;
  routeCode: string;
  routeName: string;
  morning: { stop: string; time: string | null } | null;
  evening: { stop: string; time: string | null } | null;
  plate: string | null;
}

export interface FamilyMember {
  passengerId: string;
  name: string;
  className: string | null;
  institution: string;
  guardians: { name: string; relation: string; primary: boolean; me: boolean }[];
}

export const NOTIFICATION_PREF_TYPES = [
  'TRIP_STARTED',
  'APPROACHING',
  'ARRIVED',
  'DELAY',
  'VEHICLE_CHANGED',
  'DRIVER_CHANGED',
  'CHILD_BOARDED',
  'CHILD_ARRIVED',
  'CHILD_DELIVERED',
  'CHILD_NOT_BOARDED',
  'ANNOUNCEMENT',
] as const;

export const NotificationPrefsInput = z.object({
  items: z.array(z.object({ type: z.enum(NOTIFICATION_PREF_TYPES), enabled: z.boolean(), muted: z.boolean() })).min(1),
});

export interface NotificationPref {
  type: (typeof NOTIFICATION_PREF_TYPES)[number];
  label: string;
  enabled: boolean;
  muted: boolean;
  safety: boolean;
  group: 'TRIP' | 'CHILD' | 'GENERAL';
}

export const KvkkInput = z.object({
  type: z.enum(['EXPORT', 'DELETE']),
  reason: z.string().trim().max(300).optional(),
});

/** GET /v1/trips/:id/live — P20/P21 canlı takip (yolcu) ve operasyon sefer görünümü. */
export interface TripLive {
  id: string;
  status: TripStatus;
  display: string;
  displayLabel: string;
  direction: Direction;
  date: string;
  routeCode: string;
  routeName: string;
  institution: string;
  plannedStartAt: string;
  delayMin: number;
  vehicle: { plate: string; make: string; model: string; color: string; capacity: number };
  driver: DriverPublic;
  guide: { name: string; initials: string } | null;
  position: { lat: number; lng: number; heading: number; speed: number; at: string | null } | null;
  occupancy: { boarded: number; capacity: number };
  myStop: {
    stopId: string;
    name: string;
    plannedAt: string;
    etaAt: string;
    arrivedAt: string | null;
    remainingM: number;
    passengerId: string;
    passengerName: string;
  } | null;
  nextStop: { stopId: string; name: string; etaAt: string | null } | null;
  stops: {
    stopId: string;
    order: number;
    name: string;
    address: string | null;
    lat: number;
    lng: number;
    plannedAt: string;
    etaAt: string | null;
    arrivedAt: string | null;
    departedAt: string | null;
    mine: boolean;
    boarded: number;
  }[];
  view: HomeTrip | null;
  polyline: string;
  updatedAt: string;
}

export interface BoardingPass {
  passengerId: string;
  name: string;
  /** yolcuya özel anahtar: QR/PIN cihazda üretilir (çevrimdışı da çalışır) */
  key: string;
  routeCode: string | null;
  routeName: string | null;
  plate: string | null;
}
